import {Button, Card, Col, message, Row} from 'antd';
import {
  Account as AccountType,
  AccountBrandingInput,
  useAccountQuery,
  useUpdateAccountMutation
} from 'api/generated';
import {AnchorLink, BottomActions, Hint, Spacer} from 'components';
import {ColorInput} from 'components/Form/ColorInput';
import {ImageInput} from 'components/Form/ImageInput';
import {Formik, FormikHelpers} from 'formik';
import {Form, FormItem, SubmitButton} from 'formik-antd';
import {getContrast} from 'polished';
import React from 'react';
import {useParams} from 'react-router-dom';
import * as yup from 'yup';
import {PaymentPagePreview} from './PaymentPagePreview';

const validateContrast = (colorA: string | undefined, colorB: string) => {
  if (!colorA) return true;
  try {
    return getContrast(colorA, colorB) > 2;
  } catch (e) {
    return true;
  }
};

const defaultValues = {
  accentColor: '#3d424e',
  bgColor: '#f0f4f5'
};

export const BrandingSettings: React.FC = () => {
  const [updateAccount] = useUpdateAccountMutation();
  const {accountId} = useParams<{accountId: string}>();
  const {data, loading} = useAccountQuery({
    variables: {id: accountId}
  });

  const account = data?.account as AccountType;

  if (!account || loading) {
    return <Card loading size="small" title="Branding" />;
  }

  const initialValues: AccountBrandingInput = {
    accentColor: account?.branding?.accentColor || defaultValues.accentColor,
    bgColor: account?.branding?.bgColor || defaultValues.bgColor,
    logoUrl: account?.branding?.logoUrl,
    iconUrl: account?.branding?.iconUrl
  };

  const handleSubmit = async (
    values: AccountBrandingInput,
    {setSubmitting}: FormikHelpers<AccountBrandingInput>
  ) => {
    try {
      await updateAccount({
        variables: {
          input: {id: account.id, branding: values}
        }
      });
      message.success('Branding has been updated');
    } finally {
      setSubmitting(false);
    }
  };

  const hexColorRegex = /^#([\da-f]{6}|[\da-f]{3})$/i;

  const colorValidationSchema = yup.object().shape({
    accentColor: yup
      .string()
      .matches(hexColorRegex)
      .test(
        'accentColor',
        'Contrast is too low, please select a darker color for button',
        (value?: string) => validateContrast(value, '#ffffff')
      ),
    bgColor: yup.string().matches(hexColorRegex)
  });

  function cleanupValues(values: AccountBrandingInput) {
    return {
      ...values,
      bgColor: yup.string().matches(hexColorRegex).isValidSync(values.bgColor)
        ? values.bgColor
        : initialValues.bgColor
    };
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      initialTouched={{accentColor: true}}
      validationSchema={colorValidationSchema}>
      {({values, setValues}) => (
        <Form layout="vertical">
          <Card title="Branding" size="small" style={{marginBottom: 15}}>
            <p>Customize how account brand appears in the payment page</p>
            <FormItem
              name="logoUrl"
              label={
                <Hint
                  message={
                    <span>
                      This image will be used on the payment page and emails sent to your customers.
                      SVG Images are recommended. The image resolution should be at least 400x400px.
                      Supported file types: JPG, PNG, SVG. Max file size: 1MB.
                    </span>
                  }
                  title={'Logo'}
                />
              }>
              <ImageInput
                name="logoUrl"
                maxWidth={600}
                maxHeight={400}
                minWidth={400}
                minHeight={100}
                width={300}
                bgColor={values.bgColor}
              />
            </FormItem>
            <FormItem
              name="iconUrl"
              label={
                <Hint
                  message={
                    <span>
                      This image will be used as a payment page icon and for QR code branding for{' '}
                      <AnchorLink href="https://monei.com/monei-pay/qr">MONEI Pay</AnchorLink>. SVG
                      Images are recommended. The image resolution should be 128x128px. Supported
                      file types: JPG, PNG, SVG. Max file size: 128KB.
                    </span>
                  }
                  title={'Icon'}
                />
              }>
              <ImageInput
                name="iconUrl"
                maxWidth={128}
                maxHeight={128}
                minWidth={32}
                minHeight={32}
                width={100}
                maxFileSize={128000}
              />
            </FormItem>
            <Row gutter={50}>
              <Col>
                <FormItem name="bgColor" label={'Background color'}>
                  <ColorInput name="bgColor" />
                </FormItem>
              </Col>
              <Col>
                <FormItem
                  name="accentColor"
                  label={
                    <Hint
                      message={
                        <span>
                          This color will be used for buttons and links on the payment page as well
                          as for QR code branding for{' '}
                          <AnchorLink href="https://monei.com/monei-pay/qr">MONEI Pay</AnchorLink>.
                        </span>
                      }
                      title={'Primary color'}
                    />
                  }>
                  <ColorInput name="accentColor" />
                </FormItem>
              </Col>
            </Row>
          </Card>
          <Card size="small">
            <PaymentPagePreview
              values={cleanupValues(values as AccountBrandingInput)}
              account={account}
            />
          </Card>
          <BottomActions>
            <Button onClick={() => setValues({...values, ...defaultValues})}>
              Restore defaults
            </Button>
            <Spacer />
            <SubmitButton>Save</SubmitButton>
          </BottomActions>
        </Form>
      )}
    </Formik>
  );
};
